import {
  getDefaultPeriod,
  getPackageBaseRate,
  getPackagePeriods,
  getPackagePrice,
  getPeriodLabel,
  getPeriodSuffix,
  hasPackageDiscount,
  hasPackageFreeTrial,
  getPackageTrialDays,
} from "../../../../Util/packageRates";
import { ReactComponent as TickIcon } from "../../../../Assets/Tick.svg";
import styles from "./Packages.module.scss";

interface PackageItem {
  discountRate: number;
  isFamily: boolean;
  isFreeMonth: boolean;
  isMember: boolean;
  isNonMember: boolean;
  noofMember: number;
  packageAmenities: string[];
  packageDescription: string;
  packageId: number;
  packageName: string;
  totalRates: number;
  packageRates?: {
    period: string;
    rates: number;
    discountRate?: number | null;
  }[];
  trialdays?: number | null;
}

interface Props {
  packages: PackageItem[];
  selectedPackageId: number;
  setSelectedPackageId: React.Dispatch<React.SetStateAction<number>>;
  selectedPeriod: string;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<string>>;
}

function Packages(props: Props) {
  const {
    packages,
    selectedPackageId,
    setSelectedPackageId,
    selectedPeriod,
    setSelectedPeriod,
  } = props;

  const clearSelection = () => {
    setSelectedPackageId(-1);
    setSelectedPeriod("");
  };

  const selectPackagePeriod = (pack: PackageItem, period: string) => {
    const packageId = Number(pack?.packageId);
    const isSameSelection =
      Number(selectedPackageId) === packageId &&
      String(selectedPeriod || "") === String(period || "");

    if (isSameSelection) {
      clearSelection();
      return;
    }

    setSelectedPackageId(packageId);
    setSelectedPeriod(period);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.subHeadingContainer}>
        <p className={styles.label}>Select package</p>
      </div>
      {packages?.length === 0 && (
        <div className={styles.noPackageContainer}>
          <p className={styles.text}>No package found</p>
        </div>
      )}
      <div className={styles.packagesContainer}>
        {packages?.map((pack: PackageItem, i: number) => {
          const periods = getPackagePeriods(pack);
          const isSelected =
            Number(selectedPackageId) === Number(pack?.packageId);
          const activePeriod =
            isSelected && selectedPeriod
              ? selectedPeriod
              : getDefaultPeriod(pack);
          const displayPrice = getPackagePrice(pack, activePeriod);
          const baseRate = getPackageBaseRate(pack, activePeriod);
          const showDiscount = hasPackageDiscount(pack, activePeriod);
          const suffix = getPeriodSuffix(activePeriod);
          const trialDays = getPackageTrialDays(pack);
          const showFreeTrial = hasPackageFreeTrial(pack);

          return (
            <div key={i} className={styles.package}>
              <div className={styles.infoContainer}>
                <p className={styles.name}>{pack?.packageName}</p>
                <div className={styles.priceContainer}>
                  {showDiscount ? (
                    <>
                      <p className={styles.price}>
                        ${displayPrice}
                        {suffix}
                      </p>
                      <p className={styles.cancelledPrice}>
                        ${baseRate}
                        {suffix}
                      </p>
                    </>
                  ) : (
                    <p className={styles.price}>
                      ${displayPrice}
                      {suffix}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.trialContainer}>
                {showFreeTrial && (
                  <p className={styles.label}>
                    This package has a limited time free trial - {trialDays}{" "}
                    {trialDays > 1 ? "Days" : "Day"}
                  </p>
                )}
              </div>
              {periods.length > 0 && (
                <div className={styles.periodsContainer}>
                  {periods.map((rate) => (
                    <button
                      key={rate.period}
                      type="button"
                      className={
                        isSelected &&
                        String(selectedPeriod || "") === String(rate.period || "")
                          ? styles.periodSelected
                          : styles.period
                      }
                      onClick={() => selectPackagePeriod(pack, rate.period)}
                    >
                      {getPeriodLabel(rate.period)}
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={`${styles.button} ${
                    isSelected ? styles.buttonSelected : styles.buttonDisabled
                  }`}
                  disabled={!isSelected}
                  aria-disabled={!isSelected}
                  onClick={() => {
                    if (isSelected) clearSelection();
                  }}
                >
                  {isSelected
                    ? `Selected${
                        selectedPeriod
                          ? ` · ${getPeriodLabel(selectedPeriod)}`
                          : ""
                      }`
                    : "Select"}
                </button>
              </div>
              <div className={styles.featuresContainer}>
                <p>Plan includes</p>
                {pack?.packageAmenities?.map((amenity: any, j: number) => {
                  return (
                    <div key={j} className={styles.feature}>
                      <div className={styles.iconContainer}>
                        <TickIcon className={styles.icon} />
                      </div>
                      <p className={styles.label}>{amenity}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Packages;
